import React, { useState } from 'react';
import { SimpleTabs } from 'ui/SimpleTabs';
import { FilePicker } from 'ui/FilePicker';
import { FluidButton } from 'ui/FluidButton';
import { GeneralModal } from 'ui/GeneralModal';
import { BackLink } from 'ui/BackLink';
import HelpSquareIcon from 'ui/Icons/help-square.component.svg';
import ThumbUpIcon from 'ui/Icons/thumb-up.component.svg';
import ThumbDownIcon from 'ui/Icons/thumb-down.component.svg';
import { useBankTransactionImportMutation } from './hooks/useBankTransactionImportMutation';
import { useNotifications } from 'hooks/useNotifications';
import { EBankTransactionImportStatus, IBankTransactionImportResponse, IBankTransactionImportError } from './types';
import { ImportLogTab } from './components/ImportLogTab';

interface ICsvHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CsvHelpModal: React.FC<ICsvHelpModalProps> = ({ isOpen, onClose }) => {
  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={onClose}
      modalWindowClassName="rounded max-w-[900px]"
      shouldCloseByClickingOutside={true}
    >
      <div className="p-25px pt-35px">
        <span className="block font-noe-display text-21px mb-25px">Help - CSV Format</span>
        <span className="block font-pt-sans text-15px mb-25px">
          For a correct CSV format take a look this easy sample:
        </span>
        <div className="bg-brown-prime-10 border border-dashed border-gray-40 rounded p-20px flex flex-col gap-10px">
          <span className="font-pt-sans text-14px">
            Header: Trx_Date,Value_Date,Narration,Reference,Amount,Bank_Code,Currency_Code
          </span>
          <div className="flex flex-col gap-5px">
            <span className="font-pt-sans text-14px">Rows:</span>
            <div className="flex items-center gap-5px">
              <span className="font-pt-sans text-14px">
                2025-01-31,2025-01-31,"This is a narration with commas ,,, in the
                middle",REF#-11111111,100,ABU_DHABI,AED
              </span>
              <ThumbUpIcon className="w-[18px] h-[18px] text-status-confirmed flex-shrink-0" />
            </div>
            <div className="flex items-center gap-5px">
              <span className="font-pt-sans text-14px">2025-02-28,2025-02-28,,,10000000.00,EMIRATES,EUR</span>
              <ThumbUpIcon className="w-[18px] h-[18px] text-status-confirmed flex-shrink-0" />
            </div>
            <div className="flex items-center gap-5px">
              <span className="font-pt-sans text-14px">
                2025-03-31,2025-03-31,Simple narration,REF#-22222,100000,MASHREQ,USD
              </span>
              <ThumbUpIcon className="w-[18px] h-[18px] text-status-confirmed flex-shrink-0" />
            </div>
            <div className="flex items-center gap-5px">
              <span className="font-pt-sans text-14px">
                <span className="text-status-cancelled">2025/04/30</span>,
                <span className="text-status-cancelled">2025/04/30</span>,Simple
                narration,Narration,10000000.00,PAY_BY_LINK,GBP
              </span>
              <ThumbDownIcon className="w-[18px] h-[18px] text-status-cancelled flex-shrink-0" />
            </div>
            <div className="flex items-center gap-5px">
              <span className="font-pt-sans text-14px">
                2025-03-31,2025-03-31,Simple narration,REF#-22222,
                <span className="text-status-cancelled">"1,000,000.00"</span>,MASHREQ,USD
              </span>
              <ThumbDownIcon className="w-[18px] h-[18px] text-status-cancelled flex-shrink-0" />
            </div>
            <div className="flex items-center gap-5px">
              <span className="font-pt-sans text-14px">
                2025-03-31,2025-03-31,Simple narration,Narration,10000000.00,
                <span className="text-status-cancelled">First Abu Dhabi Bank PJSC</span>,GBP
              </span>
              <ThumbDownIcon className="w-[18px] h-[18px] text-status-cancelled flex-shrink-0" />
            </div>
            <div className="flex items-center gap-5px">
              <span className="font-pt-sans text-14px">
                2025-03-31,2025-03-31,Simple narration,Narration,10000000.00,MASHREQ,
                <span className="text-status-cancelled">$</span>
              </span>
              <ThumbDownIcon className="w-[18px] h-[18px] text-status-cancelled flex-shrink-0" />
            </div>
          </div>
        </div>
        <FluidButton type="primary" onClick={onClose} className="mt-25px">
          Accept
        </FluidButton>
      </div>
    </GeneralModal>
  );
};

export const BankTransactionsImportPage: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [importResult, setImportResult] = useState<IBankTransactionImportResponse | null>(null);

  const importMutation = useBankTransactionImportMutation();
  const { showSuccessNotification, showErrorNotification } = useNotifications();

  const handleFileSelect = (files: File[]) => {
    setSelectedFile(files[0] || null);
    setImportResult(null);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    try {
      const response = await importMutation.mutateAsync(selectedFile);
      setImportResult(response);

      if (response.status === EBankTransactionImportStatus.SUCCESS) {
        showSuccessNotification('Receipt imported successfully.');
        setSelectedFile(null);
      }
    } catch (error) {
      // Handle 400 errors
      if (error.response?.data?.errors) {
        const errorData = error.response.data as IBankTransactionImportError;
        showErrorNotification(errorData.errors[0]?.message || 'Import failed');
      } else {
        showErrorNotification('An unexpected error occurred');
      }
    }
  };

  // Import button is disabled when: no file selected, loading, or already successfully imported
  const isImportDisabled =
    !selectedFile || importMutation.isPending || importResult?.status === EBankTransactionImportStatus.SUCCESS;

  // Calculate counts for the status panel
  const totalRows = importResult?.parseResult?.totalRows ?? 0;
  const errorCount = importResult?.parseResult?.rowErrors?.length ?? 0;
  const insertableCount = totalRows - errorCount;
  const hasErrors = errorCount > 0;

  return (
    <div className="bank-transactions-import-page w-auto mx-[20px] crm-desktop:mx-[80px]">
      <BackLink type="internal" href="/finance/bank-transactions">
        Back
      </BackLink>
      <div className="flex justify-between items-start mb-20px">
        <span className="font-noe-display text-4xl">Import Bank Transactions</span>
      </div>

      <SimpleTabs
        forceEqualTabWidths={true}
        className="w-full"
        tabConfig={[
          {
            title: 'New Import',
            name: 'new-import',
            styles: '',
            renderContent: () => (
              <div className="w-full">
                <span className="block font-pt-sans font-bold text-17px mb-10px">File to Import</span>
                <FilePicker
                  isAttachmentRequired={false}
                  onFileSelect={handleFileSelect}
                  onFileRemove={handleFileRemove}
                  showAttachFileLabel={false}
                  acceptString="text/csv,.csv"
                  showFileSize={true}
                  className="file-picker block w-full mt-0"
                />
                <div className="flex items-center gap-10px mt-10px">
                  <span className="text-gray-100 text-14px font-pt-sans">
                    <span className="underline">NOTE</span> CSV file with columns: Trx_Date &lt;YYYY-MM-DD&gt;,
                    Value_Date &lt;YYYY-MM-DD&gt;, Narration, Reference, Amount, Bank_Code, Currency_Code.
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsHelpModalOpen(true)}
                    className="flex-shrink-0 cursor-pointer bg-transparent outline-none border-none"
                  >
                    <HelpSquareIcon className="w-6 h-6 text-brown-prime" />
                  </button>
                </div>
                <FluidButton
                  type={selectedFile && !isImportDisabled ? 'primary' : 'secondary'}
                  onClick={handleImport}
                  disabled={isImportDisabled}
                  isLoading={importMutation.isPending}
                  className="mt-15px"
                >
                  Import
                </FluidButton>

                {importResult && (
                  <div className="bg-ivory px-20px py-15px flex flex-col gap-10px rounded mt-15px">
                    <div className="font-pt-sans text-17px">
                      <span className="font-bold">Status:</span>
                      <span
                        className={`ml-5px font-bold ${
                          importResult.status === EBankTransactionImportStatus.SUCCESS
                            ? 'text-status-confirmed'
                            : 'text-red-95'
                        }`}
                      >
                        {importResult.status === EBankTransactionImportStatus.SUCCESS ? 'Success' : 'Fail'}
                      </span>
                    </div>

                    {importResult.parseResult.fileError && (
                      <div className="text-red-95 font-pt-sans">File error: {importResult.parseResult.fileError}</div>
                    )}

                    <div
                      className={`border-l-4 pl-10px ${
                        importResult.status === EBankTransactionImportStatus.SUCCESS
                          ? 'border-l-status-confirmed'
                          : 'border-l-red-95'
                      }`}
                    >
                      <span className="font-pt-sans font-bold">transactions</span>
                      <span className="text-gray-100 ml-5px font-pt-sans">
                        {insertableCount} insertable
                        {errorCount > 0 && ` | ${errorCount} errors`}
                      </span>
                    </div>

                    {importResult.parseResult.rowErrors.map(rowError => (
                      <div key={rowError.rowNumber} className="ml-10px">
                        <span className="text-red-95 italic font-pt-sans">Row {rowError.rowNumber}</span>
                        <div className="text-gray-100 ml-10px font-pt-sans">
                          {rowError.cellErrors.map((cellError, i) => (
                            <div key={i}>
                              {cellError.header}: {cellError.message}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ),
          },
          {
            title: 'Import Log',
            name: 'import-log',
            styles: '',
            renderContent: () => <ImportLogTab />,
          },
        ]}
      />

      <CsvHelpModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />
    </div>
  );
};
